import React, { useState, createContext, useContext } from "react";
import clsx from "clsx";

const TabsContext = createContext(null);

const Tabs = ({
  children,
  defaultValue,
  value: controlledValue,
  onChange,
  className = "",
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = (newValue) => {
    if (!isControlled) {
      setUncontrolledValue(newValue);
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <TabsContext.Provider value={{ value, onChange: handleChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

Tabs.List = ({ children, className = "", variant = "default" }) => {
  const variantClasses = {
    default: "border-b border-dark-200 dark:border-dark-700",
    pills: "bg-dark-100 dark:bg-dark-800 p-1 rounded-lg",
    underline: "border-b-2 border-dark-200 dark:border-dark-700",
  };

  return (
    <div
      role="tablist"
      className={clsx("flex gap-1", variantClasses[variant], className)}
    >
      {children}
    </div>
  );
};

Tabs.Tab = ({
  children,
  value,
  disabled = false,
  className = "",
  variant = "default",
}) => {
  const context = useContext(TabsContext);
  const isActive = context?.value === value;

  const variantClasses = {
    default: clsx(
      "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
      isActive
        ? "border-primary-500 text-primary-600 dark:text-primary-400"
        : "border-transparent text-dark-500 hover:text-dark-700 hover:border-dark-300 dark:text-dark-400 dark:hover:text-dark-300"
    ),
    pills: clsx(
      "px-4 py-2 text-sm font-medium rounded-md transition-colors",
      isActive
        ? "bg-white dark:bg-dark-700 text-dark-900 dark:text-dark-100 shadow-sm"
        : "text-dark-500 hover:text-dark-700 dark:text-dark-400 dark:hover:text-dark-300"
    ),
    underline: clsx(
      "px-4 py-2 text-sm font-medium border-b-2 -mb-0.5 transition-colors",
      isActive
        ? "border-primary-500 text-primary-600 dark:text-primary-400"
        : "border-transparent text-dark-500 hover:text-dark-700 dark:text-dark-400 dark:hover:text-dark-300"
    ),
  };

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      disabled={disabled}
      onClick={() => context?.onChange(value)}
      className={clsx(
        variantClasses[variant],
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {children}
    </button>
  );
};

Tabs.Panel = ({ children, value, className = "" }) => {
  const context = useContext(TabsContext);
  const isActive = context?.value === value;

  if (!isActive) return null;

  return (
    <div role="tabpanel" className={clsx("py-4", className)}>
      {children}
    </div>
  );
};

export default Tabs;
